import { initSite, getNavOffset } from "./site.js";
import { menuSections as defaultMenuSections } from "./menu-data.js";
import {
  getCmsMenu,
  addMenuItem,
  removeMenuItem,
  addMenuSection,
  removeMenuSection,
  isAdminAuthed,
} from "./cms/store.js";
import { refreshInlineCms, showCmsToast } from "./cms/inline.js";
import { MENU_EMPTY_PLACEHOLDERS } from "./cms/menu-fields.js";

await initSite();
renderMenuPage();

function getMenuSections() {
  return getCmsMenu() ?? defaultMenuSections;
}

function renderMenuPage() {
  renderMenu();
  initMenuNav();
  initMenuAddButtons();
  initMenuRemoveButtons();
  initMenuSectionRemoveButtons();
  refreshInlineCms();
}

function renderMenu() {
  const menuSections = getMenuSections();
  const root = document.getElementById("menu-root");
  if (!root) return;

  root.innerHTML =
    menuSections
      .map(
        (section) => `
    <section class="bm-menu-section" id="${section.id}" aria-labelledby="title-${section.id}">
      <div class="bm-menu-section__head">
        <h2 class="bm-menu-section__title" id="title-${section.id}" data-cms-menu-section-title="${section.id}">${section.title}</h2>
        ${
          isAdminAuthed()
            ? `<button type="button" class="bm-cms-section-remove" data-cms-menu-section-remove="${section.id}" aria-label="הסר את ${section.title}">× הסר קטגוריה</button>`
            : ""
        }
      </div>
      <ul class="bm-menu-grid">
        ${section.items.map((item, index) => renderMenuItem(item, section.id, index)).join("")}
      </ul>
      <button type="button" class="bm-cms-menu-add" data-cms-menu-add="${section.id}">+ הוסף מנה</button>
    </section>
  `
      )
      .join("") +
    `<div class="bm-menu-cms-actions">
      <button type="button" class="bm-cms-menu-add bm-cms-menu-add--section" data-cms-menu-add-section>+ הוסף קטגוריה</button>
    </div>`;
}

function renderMenuItem(item, sectionId, index) {
  const sizes = item.sizes?.trim() ?? "";
  const desc = item.desc?.trim() ?? "";
  const removeBtn = isAdminAuthed()
    ? `<button type="button" class="bm-cms-item-remove" data-cms-menu-remove="${sectionId}" data-cms-menu-index="${index}" aria-label="הסר את ${item.name}">×</button>`
    : "";

  return `
    <li class="bm-menu-item" data-cms-menu-item data-cms-menu-section="${sectionId}" data-cms-menu-index="${index}">
      ${removeBtn}
      <div class="bm-menu-item__head">
        <div class="bm-menu-item__info">
          <h3 class="bm-menu-item__name" data-cms-menu-field="name">${item.name}</h3>
          <p class="bm-menu-item__sizes${sizes ? "" : " is-empty"}" data-cms-menu-field="sizes">${sizes || MENU_EMPTY_PLACEHOLDERS.sizes}</p>
        </div>
        <p class="bm-menu-item__price" data-cms-menu-field="price">${item.price}</p>
      </div>
      <p class="bm-menu-item__desc${desc ? "" : " is-empty"}" data-cms-menu-field="desc">${desc || MENU_EMPTY_PLACEHOLDERS.desc}</p>
    </li>
  `;
}

function initMenuRemoveButtons() {
  if (!isAdminAuthed()) return;

  document.querySelectorAll("[data-cms-menu-remove]").forEach((btn) => {
    if (btn.dataset.cmsRemoveReady === "1") return;
    btn.dataset.cmsRemoveReady = "1";

    btn.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();

      const sectionId = btn.dataset.cmsMenuRemove;
      const itemIndex = Number(btn.dataset.cmsMenuIndex);
      const name =
        btn.closest("[data-cms-menu-item]")?.querySelector("[data-cms-menu-field='name']")
          ?.textContent || "המנה";

      if (!sectionId || Number.isNaN(itemIndex) || !confirm(`להסיר את ${name}?`)) return;
      if (!removeMenuItem(sectionId, itemIndex)) {
        showCmsToast("חייבת להישאר לפחות מנה אחת בקטגוריה", "error");
        return;
      }

      renderMenuPage();
      showCmsToast("המנה הוסרה");
    });
  });
}

function initMenuSectionRemoveButtons() {
  if (!isAdminAuthed()) return;

  document.querySelectorAll("[data-cms-menu-section-remove]").forEach((btn) => {
    if (btn.dataset.cmsRemoveReady === "1") return;
    btn.dataset.cmsRemoveReady = "1";

    btn.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();

      const sectionId = btn.dataset.cmsMenuSectionRemove;
      const title =
        document.getElementById(`title-${sectionId}`)?.textContent?.trim() || "הקטגוריה";

      if (!sectionId || !confirm(`להסיר את ${title}?`)) return;
      if (!removeMenuSection(sectionId)) {
        showCmsToast("חייבת להישאר לפחות קטגוריה אחת", "error");
        return;
      }

      renderMenuPage();
      showCmsToast("הקטגוריה הוסרה");
    });
  });
}

function initMenuAddButtons() {
  if (!isAdminAuthed()) return;

  document.querySelectorAll("[data-cms-menu-add], [data-cms-menu-add-section]").forEach((btn) => {
    if (btn.dataset.cmsAddReady === "1") return;
    btn.dataset.cmsAddReady = "1";

    btn.addEventListener("click", (event) => {
      event.preventDefault();

      if (btn.hasAttribute("data-cms-menu-add-section")) {
        const sectionId = addMenuSection();
        if (!sectionId) return;
        renderMenuPage();
        showCmsToast("נוספה קטגוריה חדשה");
        requestAnimationFrame(() => {
          document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
        return;
      }

      const sectionId = btn.dataset.cmsMenuAdd;
      if (!sectionId || !addMenuItem(sectionId)) return;
      renderMenuPage();
      showCmsToast("נוספה מנה חדשה");
    });
  });
}

function initMenuNav() {
  const menuSections = getMenuSections();
  const nav = document.querySelector(".bm-menu-nav");
  if (!nav) return;

  nav.innerHTML = menuSections
    .map(
      (section) =>
        `<a class="bm-menu-nav__link" href="#${section.id}">${section.title}</a>`
    )
    .join("");

  const links = nav.querySelectorAll(".bm-menu-nav__link");
  const sections = menuSections
    .map((s) => document.getElementById(s.id))
    .filter(Boolean);

  const setActive = (id) => {
    links.forEach((link) => {
      const active = link.getAttribute("href") === `#${id}`;
      link.classList.toggle("is-active", active);
    });
  };

  if ("IntersectionObserver" in window && sections.length) {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) setActive(visible.target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.25, 0.5] }
    );
    sections.forEach((section) => observer.observe(section));
  }

  links.forEach((link) => {
    link.addEventListener("click", (event) => {
      const id = link.getAttribute("href")?.slice(1);
      const target = id ? document.getElementById(id) : null;
      if (!target) return;
      event.preventDefault();
      const menuNav = document.querySelector(".bm-menu-nav");
      const menuNavHeight = menuNav?.offsetHeight ?? 0;
      const offset = getNavOffset() + menuNavHeight + 16;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
      setActive(id);
    });
  });
}
